import { useEffect } from "react"

// tracks when parts of the overlay are mid animation, so the scheduled refresh can wait for a still moment

// per source, when it last stopped being busy (Infinity while it still is)
const busyUntilBySource = new Map<string, number>()
// sources that must never be cut off, even by a refresh that has given up waiting for idle. only for short, bounded
// animations, or a stuck one would hold the refresh back forever
const blockingSources = new Set<string>()

export const setOverlayBusy = (source: string, isBusy: boolean) => {
  if (isBusy) busyUntilBySource.set(source, Infinity)
  else if (busyUntilBySource.get(source) === Infinity) busyUntilBySource.set(source, Date.now())
}

// for fire and forget transitions with a known length
export const markOverlayBusy = (source: string, durationMs: number) => {
  const busyUntil = Date.now() + durationMs
  busyUntilBySource.set(source, Math.max(busyUntilBySource.get(source) ?? 0, busyUntil))
}

// idle once every source has been still for quietMs, which also covers follow on easing like height changes
export const isOverlayIdle = (quietMs: number) => {
  const now = Date.now()
  return Array.from(busyUntilBySource.values()).every((busyUntil) => busyUntil + quietMs <= now)
}

// true while a blocking source is mid animation
export const isOverlayBlocked = () =>
  Array.from(blockingSources).some((source) => busyUntilBySource.get(source) === Infinity)

export const useOverlayBusy = (source: string, isBusy: boolean, { blocking = false }: { blocking?: boolean } = {}) => {
  useEffect(() => {
    if (blocking) blockingSources.add(source)
    setOverlayBusy(source, isBusy)
  }, [source, isBusy, blocking])

  // an unmounted source can't still be animating
  useEffect(() => () => setOverlayBusy(source, false), [source])
}
